export interface ProjectMetric {
  value: string
  label: string
}

export interface Project {
  id: string
  name: string
  tagline: string
  location: string
  year: string | null
  disciplines: string[]
  role: string
  headline: string
  summary: string
  details: string[]
  metrics: ProjectMetric[]
  image: string | null
  imageStyle?: 'slide' | 'screen'
  gallery?: string[]
  logo: string | null
  tileColor: string
  tileText: string
}

export const projects: Project[] = [
  {
    id: 'clinica',
    name: 'Clinica.ph',
    tagline: 'AI claims filing portal',
    location: 'Philippines',
    year: null,
    disciplines: ['Health Tech', 'AI'],
    role: 'Product Design & Prototyping Lead',
    headline: 'The one where we prototyped AI claims filing for an 80-clinic network',
    summary:
      'Designed and led prototyping for an AI-assisted claims filing and payor-routing portal built to PhilHealth standards.',
    details: [
      'The portal was built to handle up to ₱700M in transaction volume.',
      'It closed the first customer of an 80-clinic network 5 months after launch.',
    ],
    metrics: [
      { value: '₱700M', label: 'Transaction capacity' },
      { value: '80', label: 'Clinic network' },
      { value: '5 mo', label: 'To first customer' },
    ],
    image: null,
    logo: null,
    tileColor: '#E9E2D3',
    tileText: '#171716',
  },
  {
    id: 'kevinroot',
    name: 'KevinRoot Medical',
    tagline: 'MedTech order system',
    location: 'Riverside, California',
    year: '2024–2026',
    disciplines: ['MedTech', 'Product Design'],
    role: 'Lead Product Designer',
    headline: 'The one where we unified ordering for 400+ orthopedic clinics',
    summary:
      'Directed the end-to-end redesign of a proprietary OMS/EHR ERP, bringing multi-vendor manufacturing and order workflows into a single system.',
    details: [
      'Worked directly with the C-suite and product leadership to scale ops workflows, contributing to +17% YoY revenue growth.',
      "Introduced code-driven, full-stack prototypes as live testing environments, aligning ops, design, and engineering and cutting approval cycles by about 90%. I also rebuilt and launched GoMFO, KevinRoot's new D2C Shopify business with 4 condition-specific orthotic SKUs and a doctor-referral funnel.",
    ],
    metrics: [
      { value: '400+', label: 'Clinics on one system' },
      { value: '$30M+', label: 'Order volume' },
      { value: '13,700+', label: 'Product customizations' },
      { value: '~90%', label: 'Faster approvals' },
    ],
    image: 'https://cdn.magicpatterns.com/uploads/6h1PVM3dabyNC5reWLPkEj/Screenshot_2026-10-26_at_8.39.41_PM.png',
    imageStyle: 'screen',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/k5QzDTR6wqF5tV2mFJHWsU/Screenshot_2026-10-26_at_8.40.44_PM.png',
      'https://cdn.magicpatterns.com/uploads/goP8LPJLAgXkBTuBSY3End/Screenshot_2026-10-26_at_8.40.18_PM.png',
    ],
    logo: null,
    tileColor: '#F3E3D6',
    tileText: '#171716',
  },
  {
    id: 'sarisuki',
    name: 'Sarisuki',
    tagline: 'Grocery e-commerce ops',
    location: 'Philippines',
    year: '2022–2024',
    disciplines: ['Ops Tech', 'Product Ownership'],
    role: 'Operations Tech & Platform Product Owner',
    headline: 'The one where we cut monthly tech burn by $7.5K',
    summary:
      "Built the foundations of the in-house fulfillment system behind all-day warehouse ops at Sarisuki, a Series A grocery platform that raised $24.8M.",
    details: [
      'I led 16 development sprints for a 13-engineer team alongside the CTO and Head of Engineering, shipping picking, packing, checkout, and admin modules.',
      'The MVP cut a 2-hour ops task down to 5 minutes. I also designed the rewards portal, account verification flow, and internal finance tools that compressed the cash conversion cycle from 45 to about 5 days.',
    ],
    metrics: [
      { value: '2h → 5m', label: 'Core ops task' },
      { value: '$7.5K', label: 'Monthly burn saved' },
      { value: '45 → 5', label: 'Days in cash cycle' },
      { value: '16', label: 'Sprints led' },
    ],
    image: 'https://cdn.magicpatterns.com/uploads/gyDK5TjVag3xdjawYyMPCJ/Screenshot_2026-10-26_at_8.19.54_PM.png',
    logo: null,
    tileColor: '#BCD08A',
    tileText: '#1F3A12',
  },
  {
    id: 'invesko',
    name: 'Invesko',
    tagline: 'Gen-Z investing app',
    location: 'Singapore',
    year: null,
    disciplines: ['Fintech', 'Design Systems'],
    role: 'Product Designer, UX Audit & Design System',
    headline: 'The one where we rebuilt onboarding to reduce churn for a Gen-Z investing app',
    summary:
      'Worked directly with the founders of Invesko, a post-seed investing platform that raised $1.6M, to make investing feel approachable for first-time investors.',
    details: [
      'We audited the end-to-end UX, then redesigned the KYC and onboarding flow, from identity verification and tax ID selection to funding and a first trade, to cut drop-off where new users churned most.',
      'I also built a cross-platform design system for iOS and Android, with shared components, templates, and success states that let the product team ship faster and more consistently.',
    ],
    metrics: [
      { value: '$1.6M', label: 'Post-seed raise' },
      { value: 'iOS + Android', label: 'One design system' },
      { value: 'KYC', label: 'Flow redesigned' },
    ],
    image: 'https://cdn.magicpatterns.com/uploads/qFipoNb9L4iijCEwakAtp4/Screenshot_2026-10-26_at_8.38.37_PM.png',
    logo: null,
    tileColor: '#000000',
    tileText: '#FFFFFF',
  },
  {
    id: 'narra',
    name: 'Narra Health',
    tagline: 'Seafarer TeleHealth',
    location: 'Singapore',
    year: null,
    disciplines: ['TeleHealth', '0→1 Product'],
    role: 'Product Lead, now Product Advisory Board',
    headline: 'The one where we built mental wellness care for 6,000+ seafarers',
    summary:
      'Led product for Narra from day one, shaping the TeleHealth platform behind its vision for seafarer mental wellness.',
    details: [
      'We launched a proof-of-concept and client pilot in under three months, then built the full platform over the following seven months for a January 2026 launch.',
      "The platform now serves 6,000+ seafarers and their families across 27 clients and 7 distribution partners, reaching $91K ARR. I sit on Narra's Product Advisory Board as the team heads into its seed raise.",
    ],
    metrics: [
      { value: '6,000+', label: 'Seafarers served' },
      { value: '$91K', label: 'ARR' },
      { value: '27', label: 'Clients' },
      { value: '7', label: 'Distribution partners' },
    ],
    image: null,
    logo: 'https://cdn.magicpatterns.com/uploads/ocLvdZc71ZNKULum6ZLUwF/Group_4.svg',
    tileColor: '#B9DCC4',
    tileText: '#171716',
  },
  {
    id: 'kinhub',
    name: 'Kinhub',
    tagline: 'Enterprise TeleHealth',
    location: 'London, UK',
    year: '2022–2023',
    disciplines: ['TeleHealth', 'Product & Design'],
    role: 'Head of Product and Design',
    headline: 'The one where we shipped 4 products in 8 months',
    summary:
      'Rebuilt the product function from the ground up at an enterprise TeleHealth company that raised $2.6M.',
    details: [
      'Scaled a cross-functional team to 7 in 6 months and shipped a B2C platform, Consultant Portal, Admin Panel, and AI Chatbot, securing 2 client pilots.',
      'Led UX research across B2C and B2B with 15 one-on-one interviews and 3 HR focus groups, and onboarded 30+ consultants with materials and direct support.',
    ],
    metrics: [
      { value: '4', label: 'Products in 8 months' },
      { value: '2', label: 'Client pilots' },
      { value: '7', label: 'Team built in 6 months' },
      { value: '30+', label: 'Consultants onboarded' },
    ],
    image: null,
    logo: 'https://cdn.magicpatterns.com/uploads/awHy4mzbEbrupQxFR7WVZ7/Group.svg',
    tileColor: '#DDE3EA',
    tileText: '#171716',
  },
  {
    id: 'museo-del-galeon',
    name: 'Museo del Galeón',
    tagline: 'Maritime museum brand',
    location: 'Philippines',
    year: '2024',
    disciplines: ['Branding', 'Web'],
    role: 'Brand & Web Lead',
    headline: 'The one where we helped land a $3M credit line for a museum',
    summary:
      'Spearheaded by the late Senator Angara, Museo del Galeón asked us to turn an abstract idea into a tangible brand for its Board of Trustees.',
    details: [
      'We ran workshops with the team led by MLQ III, museum curators, and brand strategists to shape the reimagined Museo del Galeón, then launched 2 websites as part of its marketing collateral.',
      'The brand helped secure a $3M credit line, and the museum officially opened its doors in October 2026.',
    ],
    metrics: [
      { value: '$3M', label: 'Credit line secured' },
      { value: '2', label: 'Websites launched' },
      { value: 'Oct 2026', label: 'Official opening' },
    ],
    image: 'https://cdn.magicpatterns.com/uploads/gbiWTqcP72FSuwv2YWbPnN/Screenshot_2026-10-26_at_8.20.09_PM.png',
    logo: null,
    tileColor: '#2F5A8A',
    tileText: '#F7F5EE',
  },
  {
    id: 'just-one-run',
    name: 'Just One Run',
    tagline: 'Run club and fun run',
    location: 'Philippines',
    year: '2024',
    disciplines: ['Branding', 'Events'],
    role: 'Brand Designer',
    headline: 'The one where we built a run club and fun run for 2,500 attendees',
    summary:
      'Just One Run is a run club in the south founded by national athlete and coach Ani de Leon. We built its brand and collateral, from logo to race bibs to medals.',
    details: [
      "The goal was a warm, friendly brand that leaned into the founders' femininity, with enough energy and fun to stay open to everyone.",
      'The first race took place in September 2024 with partners like Adidas, Gatorade, and Uniqlo. This 2026, Just One Run held its 3rd annual run.',
    ],
    metrics: [
      { value: '2,500', label: 'Attendees' },
      { value: '3rd', label: 'Annual run in 2026' },
      { value: '3', label: 'Brand partners' },
    ],
    image: 'https://cdn.magicpatterns.com/uploads/kjJL5LiJuCcPryKfNurfid/Screenshot_2026-10-26_at_8.21.07_PM.png',
    logo: null,
    tileColor: '#D4EC8E',
    tileText: '#1E2A4A',
  },
  {
    id: 'mind-what-matters',
    name: 'Mind What Matters',
    tagline: 'Wellbeing practice brand',
    location: 'Singapore',
    year: null,
    disciplines: ['Health & Wellness', 'Web & Print'],
    role: 'Brand & Web Designer',
    headline: 'The one where we made mental wellbeing feel calm and credible',
    summary:
      'Built the website and print collateral for Mind What Matters, a Singapore wellbeing practice.',
    details: [
      'The brief was a presence that felt warm and trustworthy to people seeking support, while staying professional enough for corporate partners.',
      'We carried one visual language across the website and printed materials so every touchpoint felt like the same practice.',
    ],
    metrics: [
      { value: 'Web', label: 'Website design' },
      { value: 'Print', label: 'Collateral suite' },
    ],
    image: null,
    logo: 'https://cdn.magicpatterns.com/uploads/x5SPAFmdV3DPBmszavcJvt/Group_1948754876.svg',
    tileColor: '#EBDDD3',
    tileText: '#171716',
  },
  {
    id: 'opus',
    name: 'OPUS',
    tagline: 'Social platform brand',
    location: 'London',
    year: null,
    disciplines: ['Social', 'Web & Brand'],
    role: 'Brand & Web Designer',
    headline: 'The one where we gave a London social platform its identity',
    summary: 'Designed the brand identity and website for OPUS, a social platform based in London.',
    details: [
      'We shaped a bold, graphic identity system and carried it through to a web presence built to grow its community.',
    ],
    metrics: [
      { value: 'Brand', label: 'Identity system' },
      { value: 'Web', label: 'Website design' },
    ],
    image: null,
    logo: null,
    tileColor: '#DCDCD6',
    tileText: '#171716',
  },
  {
    id: 'become',
    name: 'Become',
    tagline: 'Education brand',
    location: 'Madrid',
    year: null,
    disciplines: ['Educational', 'Web & Brand'],
    role: 'Brand & Web Designer',
    headline: 'The one where we built an education brand in Madrid',
    summary: 'Created the brand and website for Become, an educational company based in Madrid.',
    details: [
      'We developed an identity and web experience that felt aspirational for learners while staying clear and easy to navigate.',
    ],
    metrics: [
      { value: 'Brand', label: 'Identity system' },
      { value: 'Web', label: 'Website design' },
    ],
    image: null,
    logo: null,
    tileColor: '#E6DFD2',
    tileText: '#171716',
  },
  {
    id: 'xchange',
    name: 'xchange',
    tagline: 'Finance collateral',
    location: 'Manila',
    year: null,
    disciplines: ['Finance', 'Print'],
    role: 'Brand Designer',
    headline: 'The one where we made finance feel approachable on paper',
    summary: 'Designed print collateral for xchange, a finance company in Manila.',
    details: [
      'We translated a technical financial offer into clear, confident printed materials that the team could put in front of customers and partners.',
    ],
    metrics: [
      { value: 'Print', label: 'Collateral suite' },
      { value: 'Finance', label: 'Industry' },
    ],
    image: null,
    logo: 'https://cdn.magicpatterns.com/uploads/71Lro4NoAkxFCFdAzLk97j/Group_1948755017.svg',
    tileColor: '#E3DCE6',
    tileText: '#171716',
  },
  {
    id: 'rife',
    name: 'RIFE',
    tagline: 'Education collateral',
    location: 'Manila',
    year: null,
    disciplines: ['Educational', 'Print'],
    role: 'Brand Designer',
    headline: 'The one where we designed print collateral for an education brand',
    summary: 'Designed print collateral for RIFE, an educational organization in Manila.',
    details: [
      'We built a consistent set of printed materials around RIFE\'s distinctive mark, giving the team a ready toolkit for events and outreach.',
    ],
    metrics: [
      { value: 'Print', label: 'Collateral suite' },
      { value: 'Education', label: 'Industry' },
    ],
    image: null,
    logo: 'https://cdn.magicpatterns.com/uploads/pNdj8CivscfqTY54NqSnGq/Group_96.svg',
    tileColor: '#E6E4DA',
    tileText: '#171716',
  },
  {
    id: 'humble',
    name: 'Humble',
    tagline: 'B2B brand collateral',
    location: 'Manila',
    year: null,
    disciplines: ['B2B', 'Brand'],
    role: 'Brand Designer',
    headline: 'The one where we gave a B2B team a brand toolkit',
    summary: 'Designed brand collateral for Humble, a B2B company in Manila.',
    details: [
      'We created a set of brand materials that helped the team present itself consistently to business clients.',
    ],
    metrics: [
      { value: 'Brand', label: 'Collateral suite' },
      { value: 'B2B', label: 'Industry' },
    ],
    image: null,
    logo: null,
    tileColor: '#E9E2D3',
    tileText: '#171716',
  },
]
